import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, timeout } from 'rxjs';
import { DealerDataService } from '../../dealer-data.service';
import { LightboxService } from '@core/services/lightbox.service';
import { TenantService } from '@core/tenant/tenant.service';
import { FormService } from '@shared/widgets/form-widget/form.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'ade-veiculo',
  templateUrl: './veiculo.component.html',
  styleUrls: ['./veiculo.component.scss'],
})
export class AdeVeiculoComponent implements OnInit, OnDestroy {
  vehicle: any = null;
  productId: number | null = null;
  images: any[] = [];
  activeImage = 0;
  readonly thumbCount = 8;
  isLoading = true;
  descExpanded = false;
  showContact = false;

  contactForm: FormGroup;
  sending = false;
  feedback = '';
  feedbackError = false;

  private _unsub = new Subject<void>();

  constructor(
    private _route: ActivatedRoute,
    private _svc: DealerDataService,
    private _lightboxService: LightboxService,
    private _tenant: TenantService,
    private _fb: FormBuilder,
    private _formService: FormService,
  ) {
    this.contactForm = this._fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      message: ['', Validators.required],
    });
  }

  get showPrices(): boolean {
    return this._tenant.showPrices;
  }

  ngOnInit(): void {
    const param = this._route.snapshot.paramMap.get('slug') ?? '';
    const id = param.split('-p').pop();
    if (!id) {
      this.isLoading = false;
      return;
    }

    // timeout obrigatório: em SSR o Universal espera este HTTP para estabilizar
    // a zona antes de serializar. Sem limite, um /products/:id lento (populate=*)
    // ou o backend inacessível deixa o render pendurado para sempre — e como um
    // único processo SSR serve todos os domínios de dealer, os renders pendurados
    // acumulam e derrubam o site inteiro. O timeout aborta o XHR e deixa o render
    // completar (mostra loading/vazio; o browser volta a pedir na hidratação).
    this._svc
      .getProductById(id)
      .pipe(timeout(8000), takeUntil(this._unsub))
      .subscribe({
        next: (r: any) => {
          const raw = Array.isArray(r?.data) ? r.data[0] : r?.data;
          this.vehicle = raw?.attributes ?? null;
          this.productId = raw?.id ?? null;
          this.images = this.vehicle?.images?.data ?? [];
          this.isLoading = false;
          this._lightboxService.refresh();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this._unsub.next();
    this._unsub.complete();
  }

  /** Envia pedido de informação do veículo para o vendedor (dealer) via /messages. */
  submit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    const receiver = this._tenant.profile?.id;
    if (!receiver) {
      this.feedback = 'Não foi possível enviar. Tente mais tarde.';
      this.feedbackError = true;
      return;
    }

    const v = this.contactForm.value;
    this.sending = true;
    this.feedback = '';
    this.feedbackError = false;

    const payload: any = {
      data: {
        title: `Nova mensagem no veículo ${this.name} de ${v.name}`,
        message: v.message,
        receiver,
        guest_email: v.email,
        data: {
          url: typeof window !== 'undefined' ? window.location.href : '',
          firstname: v.name,
          lastname: '',
          email: v.email,
          phone: v.phone,
          message: v.message,
          product: this.productId,
        },
      },
    };
    if (this.productId) payload.data.product = this.productId;

    this._formService.sendMessageToVendor(payload).subscribe({
      next: () => {
        this.sending = false;
        this.feedbackError = false;
        this.feedback = 'Mensagem enviada com sucesso!';
        this.contactForm.reset();
      },
      error: () => {
        this.sending = false;
        this.feedbackError = true;
        this.feedback = 'Ocorreu um erro. Tente novamente.';
      },
    });
  }

  get name(): string {
    if (this.vehicle?.name) return this.vehicle.name;
    const brand =
      this.vehicle?.product_brand?.data?.attributes?.name ||
      this.vehicle?.customvalue_brand ||
      '';
    const model =
      this.vehicle?.product_model?.data?.attributes?.name ||
      this.vehicle?.customvalue_model ||
      '';
    return `${brand} ${model}`.trim();
  }

  get specs(): Array<{ label: string; value: string }> {
    const a = this.vehicle;
    if (!a) return [];
    const rows: Array<{ label: string; value: any }> = [];

    // Todos os atributos dinâmicos do produto (combustível, cor, cv, km, transmissão, …).
    // Marca, modelo, ano e preço são omitidos de propósito (aparecem no cabeçalho).
    const attrs = a.attributes;
    if (Array.isArray(attrs)) {
      for (const item of attrs) {
        const def = item?.attribute?.data?.attributes;
        const label = def?.friendlyName || def?.name;
        const value =
          item?.attribute_value?.data?.attributes?.value ?? item?.customValue;
        if (label && value != null && value !== '') {
          rows.push({ label, value });
        }
      }
    }

    // Specs úteis que não vivem nos atributos dinâmicos.
    rows.push(
      { label: 'CONDIÇÃO', value: a.product_condition?.data?.attributes?.name },
      {
        label: 'CERTIFICADO',
        value: a.isCertified != null ? (a.isCertified ? 'Sim' : 'Não') : null,
      },
      {
        label: 'IMPORTADO',
        value: a.isNacional != null ? (a.isNacional ? 'Não' : 'Sim') : null,
      },
    );

    return rows.filter((r) => r.value != null && r.value !== '');
  }

  // Pool da galeria sem a imagem principal (activeImage) — a imagem grande
  // nunca se repete nas thumbs nem na galeria completa.
  private get _galleryPool(): any[] {
    return this.images.filter((_, i) => i !== this.activeImage);
  }

  get thumbImages(): any[] {
    return this._galleryPool.slice(0, this.thumbCount);
  }

  get restImages(): any[] {
    return this._galleryPool.slice(this.thumbCount);
  }

  imgUrl(url: string): string {
    if (!url) return '';
    return url.startsWith('http') ? url : `${environment.assets}${url}`;
  }

  share(): void {
    if (navigator.share) {
      navigator.share({ title: this.name, url: window.location.href });
    } else {
      navigator.clipboard?.writeText(window.location.href);
    }
  }

  shareWhatsapp(): void {
    const text = `${this.name} - ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }

  shareEmail(): void {
    const subject = this.name;
    const body = window.location.href;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }
}
